import React from "react";
import { Box, Modal, Divider, CircularProgress } from "@mui/joy";
import type { FollowUpSheet, Dive, Module, LogEntry } from "../../../api/followup/Followup.typed";
import type { Comment } from "../../../api/zekay/comments";
import { useFollowup } from "../../../contexts/api";
import { useAuth } from "../../../contexts/api";
import { useAlert } from "../../../contexts/app/notificationToast";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import { userDisplayName } from "../../../utils/userHelper";
import { sortCriteria } from "./helpers/followupCriteriaHelpers";
import {
  normalizeNumericInput,
  normalizeDivesForSave,
  newDiveDefaultDate,
} from "./helpers/followupDiveHelpers";
import FollowUpFormHeader from "./display/FollowUpForm/FollowUpFormHeader";
import FollowUpDivesCard from "./display/FollowUpForm/FollowUpDivesCard";
import FollowUpLogsAccordion from "./display/FollowUpForm/FollowUpLogsAccordion";
import FollowUpModuleSection from "./display/FollowUpForm/FollowUpModuleSection";
import FullScreenCommentModal from "./display/FollowUpForm/FullScreenCommentModal";
import DiveCommentPopupModal from "./display/FollowUpForm/DiveCommentPopupModal";
import { colorTokens, textTokens } from "../../../styles/globalStyles";

type FollowUpFormProps = {
  followUpSheet: FollowUpSheet;
  open: boolean;
  onClose: () => void;
  onSave: (updatedFollowUpSheet: FollowUpSheet) => void;
  customerName?: string;
  trainingTitle?: string;
  canEdit?: boolean;
  initialMode?: "view" | "edit";
};

type EditSnapshot = { form: { modules: Module[] }; dives: Dive[] };

export default function FollowUpForm({
  followUpSheet,
  open,
  onClose,
  onSave,
  customerName,
  trainingTitle,
  canEdit = false,
  initialMode = "view",
}: FollowUpFormProps) {
  const templateId = followUpSheet.TrainingTemplateID;
  // Deep copy: criteria handlers mutate nested objects, which must not leak into the parent sheet.
  const [form, setForm] = React.useState<{ modules: Module[] }>(() =>
    structuredClone(followUpSheet.Form ?? { modules: [] }),
  );
  const [isEditingState, setIsEditingState] = React.useState(initialMode === "edit");
  const isEditing = isEditingState && canEdit;
  const editSnapshotRef = React.useRef<EditSnapshot | null | undefined>(undefined);
  if (editSnapshotRef.current === undefined) {
    editSnapshotRef.current =
      initialMode === "edit"
        ? { form: structuredClone(followUpSheet.Form ?? { modules: [] }), dives: [] }
        : null;
  }
  const [dives, setDives] = React.useState<Dive[]>([]);
  const [logs, setLogs] = React.useState<LogEntry[]>(followUpSheet.Logs ?? []);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);

  const [fullScreenComment, setFullScreenComment] = React.useState<{
    diveIndex: number;
    value: string;
  } | null>(null);

  const commentAnchorRefs = React.useRef<(HTMLElement | null)[]>([]);

  const [commentPopup, setCommentPopup] = React.useState<{
    open: boolean;
    diveIndex: number;
    comment: string;
    position: { top: number; left: number } | null;
  }>({
    open: false,
    diveIndex: -1,
    comment: "",
    position: null,
  });

  const { user } = useAuth();
  const { updateFollowUpSheet, getDives, updateDives } = useFollowup();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();

  const getDivesRef = React.useRef(getDives);
  const showAlertRef = React.useRef(showAlert);
  getDivesRef.current = getDives;
  showAlertRef.current = showAlert;

  // No prop→state sync effect: parent unmounts the modal on close (`{open && <FollowUpForm/>}`),
  // so the `useState` lazy initializer above always seeds from the latest sheet on (re)mount.
  // `handleSave` rewrites `form`/`logs` on success — re-running an effect on every parent
  // re-render would clobber unsaved edits.

  // Load dives only when modal opens or credit/customer change (not on every context re-render after save)
  React.useEffect(() => {
    async function loadDives() {
      if (followUpSheet.CustomerId && templateId != null) {
        try {
          setIsLoading(true);
          const loadedDives = await getDivesRef.current(templateId, followUpSheet.CustomerId);
          setDives(loadedDives ?? []);
          if (editSnapshotRef.current) {
            editSnapshotRef.current = {
              ...editSnapshotRef.current,
              dives: structuredClone(loadedDives ?? []),
            };
          }
        } catch {
          showAlertRef.current("Impossible de charger les plongées", "danger");
        } finally {
          setIsLoading(false);
        }
      }
    }
    loadDives();
  }, [open, templateId, followUpSheet.CustomerId]);

  const handleDiveChange = React.useCallback(
    (index: number, field: keyof Dive, value: string | number | Date) => {
      setDives((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: value };
        return next;
      });
    },
    [],
  );

  const handleDeleteDive = React.useCallback(
    async (index: number) => {
      if (!isEditing || !followUpSheet.CustomerId || templateId == null) return;
      const ok = await showConfirmation(
        "Supprimer la plongée",
        "La plongée sera supprimée immédiatement.",
      );
      if (!ok) return;

      const updatedDives = dives
        .filter((_, i) => i !== index)
        .map((dive, idx) => ({
          ...dive,
          Number: idx + 1,
        }));
      const toSave = normalizeDivesForSave(updatedDives);
      try {
        const savedDives = await updateDives(templateId, followUpSheet.CustomerId, toSave);
        const nextDives = savedDives ?? updatedDives;
        setDives(nextDives);
        // Deletion persists all dives, so the cancel restore point must match the server.
        if (editSnapshotRef.current) {
          editSnapshotRef.current = {
            ...editSnapshotRef.current,
            dives: structuredClone(nextDives),
          };
        }
      } catch {
        showAlert("Erreur lors de la suppression de la plongée", "danger");
      }
    },
    [
      isEditing,
      followUpSheet.CustomerId,
      templateId,
      dives,
      updateDives,
      showConfirmation,
      showAlert,
    ],
  );

  const handleAddDive = React.useCallback(() => {
    if (!isEditing || !followUpSheet.CustomerId || templateId == null) return;
    const newDive: Dive = {
      CustomerID: followUpSheet.CustomerId,
      TrainingTemplateID: templateId,
      Number: dives.length + 1,
      Date: newDiveDefaultDate(),
      Instructor: userDisplayName(user) || "",
      DiveSite: "",
      DiveTime: null,
      DiveDepth: null,
      Comment: "",
    };
    setDives((prev) => [...prev, newDive]);
  }, [isEditing, followUpSheet.CustomerId, templateId, dives.length, user]);

  const handleCheckboxChange = React.useCallback(
    (moduleIndex: number, criteriaIndex: number, option: string) => {
      setForm((prev) => {
        const updated = { ...prev };
        const status = updated.modules[moduleIndex].criteria[criteriaIndex].status ?? [];
        updated.modules[moduleIndex].criteria[criteriaIndex].status = status.includes(option)
          ? status.filter((o) => o !== option)
          : [...status, option];
        return updated;
      });
    },
    [],
  );

  const handleRadioChange = React.useCallback(
    (moduleIndex: number, criteriaIndex: number, value: string) => {
      setForm((prev) => {
        const updated = { ...prev };
        updated.modules[moduleIndex].criteria[criteriaIndex].status = [value];
        return updated;
      });
    },
    [],
  );

  const updateCriterionComments = React.useCallback(
    (moduleIndex: number, criteriaIndex: number, update: (comments: Comment[]) => Comment[]) => {
      if (!isEditing) return;
      setForm((prev) => {
        const updated = { ...prev };
        const criteria = updated.modules[moduleIndex].criteria[criteriaIndex];
        criteria.comments_array = update(criteria.comments_array ?? []);
        return updated;
      });
    },
    [isEditing],
  );

  const handleAddComment = React.useCallback(
    (moduleIndex: number, criteriaIndex: number, text: string) =>
      updateCriterionComments(moduleIndex, criteriaIndex, (comments) => [
        ...comments,
        {
          user: userDisplayName(user) || "User",
          comment: text,
          timestamp: new Date().toISOString(),
        },
      ]),
    [updateCriterionComments, user],
  );

  const handleUpdateComment = React.useCallback(
    (moduleIndex: number, criteriaIndex: number, commentIndex: number, text: string) =>
      updateCriterionComments(moduleIndex, criteriaIndex, (comments) =>
        comments.map((comment, i) =>
          i === commentIndex
            ? { ...comment, comment: text, timestamp: new Date().toISOString() }
            : comment,
        ),
      ),
    [updateCriterionComments],
  );

  const handleDeleteComment = React.useCallback(
    async (moduleIndex: number, criteriaIndex: number, commentIndex: number) => {
      const ok = await showConfirmation(
        "Supprimer le commentaire",
        "Le commentaire sera retiré à l'enregistrement de la fiche.",
      );
      if (!ok) return;
      updateCriterionComments(moduleIndex, criteriaIndex, (comments) =>
        comments.filter((_, i) => i !== commentIndex),
      );
    },
    [showConfirmation, updateCriterionComments],
  );

  const handleSave = React.useCallback(async () => {
    if (!isEditing) return;
    const sortedForm = form?.modules
      ? {
          ...form,
          modules: form.modules.map((module) => ({
            ...module,
            criteria: sortCriteria(module.criteria),
          })),
        }
      : form;

    // Local logs: the API appends to the logs it receives, and the parent prop may lag behind.
    const updatedFollowUpSheet = {
      ...followUpSheet,
      Form: sortedForm,
      Logs: logs,
    };

    setIsSaving(true);
    try {
      const savedSheet = await updateFollowUpSheet(updatedFollowUpSheet);
      let nextDives = dives;
      if (followUpSheet.CustomerId && templateId != null && dives.length > 0) {
        const savedDives = await updateDives(
          templateId,
          followUpSheet.CustomerId,
          normalizeDivesForSave(dives),
        );
        if (savedDives) nextDives = savedDives;
      }
      const finalSheet = savedSheet ?? updatedFollowUpSheet;
      const nextForm = finalSheet.Form ?? sortedForm;
      onSave(finalSheet);
      setForm(nextForm);
      setDives(nextDives);
      if (Array.isArray(finalSheet.Logs)) {
        setLogs(finalSheet.Logs);
      }
      // Stay in edit mode; the last save becomes the new restore point.
      editSnapshotRef.current = {
        form: structuredClone(nextForm),
        dives: structuredClone(nextDives),
      };
      showAlert("Fiche de suivi enregistrée avec succès", "success");
    } catch {
      showAlert("Erreur lors de l'enregistrement", "danger");
    } finally {
      setIsSaving(false);
    }
  }, [
    isEditing,
    form,
    followUpSheet,
    logs,
    dives,
    templateId,
    updateFollowUpSheet,
    updateDives,
    onSave,
    showAlert,
  ]);

  const handleStartEdit = React.useCallback(() => {
    if (!canEdit) return;
    editSnapshotRef.current = { form: structuredClone(form), dives: structuredClone(dives) };
    setIsEditingState(true);
  }, [canEdit, form, dives]);

  const snapshot = editSnapshotRef.current;
  const isDirty =
    isEditing &&
    snapshot != null &&
    (JSON.stringify(form) !== JSON.stringify(snapshot.form) ||
      JSON.stringify(dives) !== JSON.stringify(snapshot.dives));

  const restoreSnapshot = React.useCallback(() => {
    const current = editSnapshotRef.current;
    if (current) {
      setForm(structuredClone(current.form));
      setDives(structuredClone(current.dives));
    }
  }, []);

  const handleCancelChanges = React.useCallback(async () => {
    const ok = await showConfirmation(
      "Annuler les modifications",
      "Les modifications depuis le dernier enregistrement seront perdues.",
    );
    if (ok) restoreSnapshot();
  }, [showConfirmation, restoreSnapshot]);

  const handleExitEdit = React.useCallback(async () => {
    if (isDirty) {
      const ok = await showConfirmation(
        "Quitter l'édition",
        "Les modifications non enregistrées seront perdues.",
      );
      if (!ok) return;
      restoreSnapshot();
    }
    editSnapshotRef.current = null;
    setIsEditingState(false);
  }, [isDirty, showConfirmation, restoreSnapshot]);

  const handleRequestClose = React.useCallback(async () => {
    if (isDirty) {
      const ok = await showConfirmation(
        "Quitter sans enregistrer",
        "Les modifications non enregistrées seront perdues.",
      );
      if (!ok) return;
    }
    onClose();
  }, [isDirty, showConfirmation, onClose]);

  const handleResetCriteria = React.useCallback((moduleIndex: number, criteriaIndex: number) => {
    setForm((prev) => {
      const updated = { ...prev };
      updated.modules[moduleIndex].criteria[criteriaIndex].status = [];
      return updated;
    });
  }, []);

  const handleFullScreenCommentSave = React.useCallback(() => {
    if (fullScreenComment) {
      handleDiveChange(fullScreenComment.diveIndex, "Comment", fullScreenComment.value);
      setFullScreenComment(null);
    }
  }, [fullScreenComment, handleDiveChange]);

  const handleCommentClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>, index: number, comment: string) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const isMobile = window.innerWidth < 600;
      const popupWidth = isMobile ? 280 : 350;
      const leftPos = rect.left + rect.width / 2 - popupWidth / 2;
      const adjustedLeft = Math.min(Math.max(leftPos, 20), window.innerWidth - popupWidth - 20);
      setCommentPopup({
        open: true,
        diveIndex: index,
        comment: comment ?? "",
        position: {
          top: isMobile ? Math.max(rect.top - 250, 20) : rect.top - 10,
          left: adjustedLeft,
        },
      });
    },
    [],
  );

  const handleCommentPopupClose = React.useCallback(
    (save: boolean) => {
      if (save && isEditing && commentPopup.diveIndex >= 0) {
        handleDiveChange(commentPopup.diveIndex, "Comment", commentPopup.comment);
      }
      setCommentPopup({
        open: false,
        diveIndex: -1,
        comment: "",
        position: null,
      });
    },
    [isEditing, commentPopup.diveIndex, commentPopup.comment, handleDiveChange],
  );

  const modalSlotProps = React.useMemo(() => ({ backdrop: { sx: { opacity: 0.1 } } }), []);

  const onFullScreenValueChange = React.useCallback((value: string) => {
    setFullScreenComment((prev) => (prev ? { ...prev, value } : null));
  }, []);

  const onDiveCommentChange = React.useCallback((value: string) => {
    setCommentPopup((prev) => ({ ...prev, comment: value }));
  }, []);

  const closeFullScreenComment = React.useCallback(() => {
    setFullScreenComment(null);
  }, []);

  return (
    <Modal
      open={open}
      onClose={() => void handleRequestClose()}
      disableScrollLock
      keepMounted={false}
      slotProps={modalSlotProps}
    >
      <>
        <Box
          sx={{
            position: "fixed",
            top: 10,
            left: 10,
            right: 10,
            height: "calc(100% - 30px)",
            overflow: "auto",
            borderRadius: "10px",
            backgroundColor: colorTokens.bg.sheetLight,
            color: textTokens.color.inverse,
            "& .MuiTypography-root": { color: textTokens.color.inverse },
            "& .MuiInputBase-input, & textarea, & input": { color: textTokens.color.inverse },
            "& .MuiCard-root, & .MuiSheet-root, & .MuiAccordion-root, & .MuiAccordionDetails-root, & .MuiAccordionSummary-root":
              {
                backgroundColor: colorTokens.bg.sheetLight,
                color: textTokens.color.inverse,
              },
            "& .MuiInput-root": {
              backgroundColor: colorTokens.bg.sheetLight,
              borderColor: "#D8D8D8",
              color: textTokens.color.inverse,
            },
            "& .MuiInput-root:hover, & .MuiInput-root.Mui-focused": {
              borderColor: "#BDBDBD",
            },
            "& .MuiCheckbox-checkbox": {
              backgroundColor: colorTokens.bg.sheetLight,
              borderColor: "#BDBDBD",
              color: textTokens.color.inverse,
            },
            "& .MuiCheckbox-label, & .MuiRadio-label": {
              color: textTokens.color.inverse,
            },
            "& .MuiRadio-radio": {
              backgroundColor: colorTokens.bg.sheetLight,
              borderColor: "#BDBDBD",
            },
            "& input[type='date']::-webkit-calendar-picker-indicator": {
              filter: "invert(0)",
              opacity: 1,
              cursor: "pointer",
            },
          }}
        >
          {isSaving && (
            <Box
              sx={{
                position: "fixed",
                top: 10,
                left: 10,
                right: 10,
                bottom: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.82)",
                borderRadius: "10px",
                zIndex: 1400,
              }}
            >
              <CircularProgress size="lg" />
            </Box>
          )}
          <FollowUpFormHeader
            customerName={customerName}
            trainingTitle={trainingTitle}
            isEditing={isEditing}
            canEdit={canEdit}
            isDirty={isDirty}
            onEdit={handleStartEdit}
            onExitEdit={() => void handleExitEdit()}
            onSave={handleSave}
            onCancelChanges={() => void handleCancelChanges()}
            onClose={() => void handleRequestClose()}
            disabled={isSaving}
          />

          <Box sx={{ p: "1rem" }}>
            <FollowUpDivesCard
              dives={dives}
              isLoading={isLoading}
              commentAnchorRefs={commentAnchorRefs}
              onDiveChange={
                handleDiveChange as React.ComponentProps<typeof FollowUpDivesCard>["onDiveChange"]
              }
              onNumericInput={normalizeNumericInput}
              onDeleteDive={handleDeleteDive}
              onAddDive={handleAddDive}
              onCommentClick={handleCommentClick}
              readOnly={!isEditing}
            />

            {logs && logs.length > 0 && <FollowUpLogsAccordion logs={logs} />}

            <Divider sx={{ my: 2 }} />

            {form.modules.map((module, moduleIndex) => (
              <FollowUpModuleSection
                key={moduleIndex}
                module={module}
                moduleIndex={moduleIndex}
                onCheckboxChange={handleCheckboxChange}
                onRadioChange={handleRadioChange}
                onResetCriteria={handleResetCriteria}
                onAddComment={handleAddComment}
                onUpdateComment={handleUpdateComment}
                onDeleteComment={(moduleIdx, criteriaIdx, commentIdx) =>
                  void handleDeleteComment(moduleIdx, criteriaIdx, commentIdx)
                }
                userName={userDisplayName(user)}
                readOnly={!isEditing}
              />
            ))}
          </Box>
        </Box>

        <FullScreenCommentModal
          open={fullScreenComment !== null}
          value={fullScreenComment?.value ?? ""}
          onValueChange={onFullScreenValueChange}
          onSave={handleFullScreenCommentSave}
          onClose={closeFullScreenComment}
        />

        <DiveCommentPopupModal
          open={commentPopup.open}
          comment={commentPopup.comment}
          position={commentPopup.position}
          onCommentChange={onDiveCommentChange}
          onClose={handleCommentPopupClose}
        />
      </>
    </Modal>
  );
}
